/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


// ===============================
// CUSTOM COUNTRY LOGIC
// ===============================

const conversionRateMap = {
  SINGAPORE: {
    currency: "SG$",
    rate: 1.35,
  },
  "UNITED ARAB EMIRATES": {
    currency: "AED",
    rate: 3.8,
  },
  "UNITED KINGDOM": {
    currency: "£",
    rate: 0.79,
  },
  "NEW ZEALAND": {
    currency: "NZ$",
    rate: 1.81,
  },
  CANADA: {
    currency: "CA$",
    rate: 1.45,
  },
  AUSTRALIA: {
    currency: "AU$",
    rate: 1.63,
  },
  CYPRUS: {
    currency: "€",
    rate: 0.87,
  },
  ESTONIA: {
    currency: "€",
    rate: 0.87,
  },
  FINLAND: {
    currency: "€",
    rate: 0.87,
  },
  FRANCE: {
    currency: "€",
    rate: 0.87,
  },
  GERMANY: {
    currency: "€",
    rate: 0.87,
  },
  IRELAND: {
    currency: "€",
    rate: 0.87,
  },
  ITALY: {
    currency: "€",
    rate: 0.87,
  },
  LATVIA: {
    currency: "€",
    rate: 0.87,
  },
  LITHUANIA: {
    currency: "€",
    rate: 0.87,
  },
  LUXEMBOURG: {
    currency: "€",
    rate: 0.87,
  },
  MALTA: {
    currency: "€",
    rate: 0.87,
  },
  NETHERLANDS: {
    currency: "€",
    rate: 0.87,
  },
  PORTUGAL: {
    currency: "€",
    rate: 0.87,
  },
  "SAN MARINO": {
    currency: "€",
    rate: 0.87,
  },
  SLOVAKIA: {
    currency: "€",
    rate: 0.87,
  },
  SLOVENIA: {
    currency: "€",
    rate: 0.87,
  },
  SPAIN: {
    currency: "€",
    rate: 0.87,
  },
  "VATICAN CITY": {
    currency: "€",
    rate: 0.87,
  },
  REST: {
    currency: "$",
    rate: 1,
  },
};

function extractAmount(str) {
  const cleaned = str.replace(/[^\d.,-]/g, '').trim();
  const normalized = cleaned.replace(/,/g, '');
  return normalized;
}

function convertAmounts(country, items) {
  const converted = conversionRateMap[country] || conversionRateMap["REST"];
  for (let i = 0; i < items.length; i++) {
    const amountToConvert = extractAmount(items[i].innerHTML);
    const formattedConverted = Math.ceil(amountToConvert * converted.rate).toFixed(0);
    items[i].innerHTML =
      converted.currency + formattedConverted;
  }
}

function handleShippingReturnsVisibility(country, items) {
  const normalizedCountry = country.toLowerCase();
  const list = Array.from(items);
  list.forEach(item => {
    const area = item.dataset.area.toLowerCase();
    if (area.includes(normalizedCountry)) {
      item.style.display = 'list-item';
    }
  });
}

document.addEventListener("DOMContentLoaded", function () {
  const shippingReturnsItems = document.getElementsByClassName(
    "shipping-returns-item"
  );
  const convertiblePrices = document.getElementsByClassName(
    "currency_convertible"
  );

  const itemsToProcess = [...shippingReturnsItems, ...convertiblePrices];
  if (itemsToProcess && itemsToProcess.length > 0) {
    fetch("https://robify.ezapp.ovh/country?justcountry=1")
      .then((response) => response.text())
      .then((country) => {
        try {
          let countryProcessed = country.split(", ");
          countryProcessed = countryProcessed[0].split(" (")[0];

          if (shippingReturnsItems.length > 0) {
            handleShippingReturnsVisibility(countryProcessed, shippingReturnsItems);
          }

          if (convertiblePrices.length > 0) {
            convertAmounts(countryProcessed, convertiblePrices);
          }
        } catch (e) {
          console.error("Unable to handle custom country logic:", e);
        }
      });
  }
});

// ===============================
// REVIEWS LOGIC
// ===============================

function addReviewImageListener() {
  const reviewImages = document.getElementsByClassName("review_img");
  const modal = document.getElementById("reviewImageModal");
  const modalImg = document.getElementById("reviewImageModalImg");
  const closeBtn = document.querySelector(".review-img-modal-close");

  for (let i = 0; i < reviewImages.length; i++) {
    reviewImages[i].addEventListener("click", function () {
      modalImg.src = this.src;
      modal.style.display = "flex";
    });
  }

  // Close on close button click
  closeBtn.addEventListener("click", function () {
    modal.style.display = "none";
    modalImg.src = "";
  });

  // Close when clicking outside the image
  modal.addEventListener("click", function (e) {
    if (e.target === modal) {
      modal.style.display = "none";
      modalImg.src = "";
    }
  });
}

function readMore(id) {
	document.getElementById("d-"+id).style.display = "inline";
	document.getElementById("a-"+id).style.display = "none";
}

var xhttp;
xhttp = new XMLHttpRequest();
xhttp.onreadystatechange = function() {
	if (this.readyState == 4 && this.status == 200) {
    var link = document.createElement( "link" );
    link.href = "https://reviewox.ezapp.ovh/css/v3.css";
    link.type = "text/css";
    link.rel = "stylesheet";

    document.getElementsByTagName( "head" )[0].appendChild( link );
    document.getElementById("reviewox_v3").innerHTML = this.responseText;
    addReviewImageListener();
	}
};
url = window.review_url;
// BUGFIX: only request when review_url is a valid URL (homepage / products with no reviews
// leave it undefined) — prevents GET /undefined 404.
if (typeof url === "string" && url && url !== "undefined" && url.indexOf("undefined") === -1) {
  xhttp.timeout = 5000;
  xhttp.open("GET", url, true);
  xhttp.send();
}

function loadMore() {
	if (document.getElementById("next_url") != null) {
		url = document.getElementById("next_url").href;
		document.getElementById("next_url").parentNode.removeChild(document.getElementById("next_url"));
		var xhttp;
		xhttp = new XMLHttpRequest();
		xhttp.onreadystatechange = function() {
			if (this.readyState == 4 && this.status == 200) {
				// document.getElementById("block").innerHTML += this.responseText.split("<!-- GRID -->")[1].split(`<div style="clear:both;">`)[0].split("");
				document.getElementById("block").innerHTML += this.responseText.split("<!-- review code start -->")[1].split(`<!-- review code end -->`)[0];
				if (document.getElementById("next_url") == null) {
					document.getElementById("loadMore").parentNode.removeChild(document.getElementById("loadMore"));
				}
        addReviewImageListener();
			}
		};
		xhttp.open("GET", url, true);
		xhttp.send();
    
	}
}

// ===============================
// FREE GIFT LOGIC
// Addition / Removal of Free Gift
// lives in main.js
// ===============================

async function handleFreeGiftManualChanges(quantity) {
  const giftUpdate = {
    updates:{
      [freeGiftId]: quantity
    }
  };

  const giftRes = await fetch(theme.routes.cartUpdate, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(giftUpdate)
  });

  if (!giftRes.ok) throw new Error(giftRes.status);

  const updatedCart = await giftRes.json();

  localStorage.setItem('free-gift-removed', quantity === 0 ? true : false);

  document.dispatchEvent(
    new CustomEvent('on:cart:change', { 
      bubbles: true,
    })
  );
}

function handleProgressBarProgression(cart) {

  const currentTotal = cart.total_price;
  let currentStage = 0;
  if (currentTotal >= freeGiftThreshold) {
    currentStage = 2;
  } else if (currentTotal >= freeShippingThreshold) {
    currentStage = 1;
  }

  // Handle Progress Bar Width
  const progressBar = document.getElementById('cart-progress-bar');
  if (!progressBar) {
    return;
  }

  if (progressBar.style.display != 'block') {
    progressBar.style.display = 'block';
  }

  const fill = progressBar.querySelector('.cart-progress-fill');
  const steps = progressBar.querySelectorAll('.step');

  let percent = 20;
  if (currentStage === 1) percent = 75;
  if (currentStage === 2) percent = 100;

  fill.style.width = `${percent}%`;

  steps.forEach(step => {
    const stepIndex = parseInt(step.dataset.step, 10);
    step.classList.toggle('active', stepIndex <= currentStage);
  });

  // Handle Progress Bar Text
  const cartFreeShippingMessage = document.getElementById('cart-free-shipping-message');
  const freeGiftRemoved = localStorage.getItem('free-gift-removed');

  if (cartFreeShippingMessage) {
    if (currentStage === 0) {
      const difference = ((freeShippingThreshold - currentTotal) / 100).toFixed(2);
      const formattedRate = theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, difference);
      cartFreeShippingMessage.innerHTML = `You're <strong>${formattedRate}</strong> away from <strong>Free Shipping!</strong>`;
    } else if (currentStage === 1) {
      const difference = ((freeGiftThreshold - currentTotal) / 100).toFixed(2);
      const formattedRate = theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, difference);
      cartFreeShippingMessage.innerHTML = `You're <strong>${formattedRate}</strong> away from a <strong>Free Gift!</strong>`;
    } else if (currentStage === 2) {
      cartFreeShippingMessage.innerHTML = `🎉 Congratulations! You've unlocked a <strong>Free Gift & Free Shipping!</strong>`;
      if (freeGiftRemoved == "true") {
        cartFreeShippingMessage.innerHTML += `<br />You removed your free gift, but you can always <span class="free-gift-add-back" onclick="handleFreeGiftManualChanges(1)">add it back</span>.`
      }
    }
  }
}

async function handleProgressBarEvent(event) {
  if (enableFreeGiftFunctionality && enableFreeShippingFunctionality) {
    try {
      const cart = await fetch(`${theme.routes.cart}.js`).then(r => r.json());
      handleProgressBarProgression(cart);
    } catch (e) {
      const progressBar = document.getElementById('cart-progress-bar');
      if (progressBar) {
        progressBar.style.display = 'none';
      }
    }
  }
}

document.addEventListener('on:cart:add', handleProgressBarEvent);
document.addEventListener('on:cart:change', handleProgressBarEvent);

document.addEventListener('DOMContentLoaded', async () => {
  try {
    const cart = await fetch(`${theme.routes.cart}.js`).then(r => r.json());
    await handleFreeGiftExistence(cart);
    await handleProgressBarProgression(cart);
  } catch (e) {
    console.error("Error happened while updating cart drawer", e);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver((mutations, obs) => {
    const target = document.querySelector('.kaching-bundles');
  
    console.log("OBSERVING");
    if (target) {

      console.log("FOUND THE ELEMENT");
      const stickyEl = document.querySelector('.product-form.sticky-content-container');
      
      console.log("STICKY ELEMENT", stickyEl);
      if (stickyEl) {
        stickyEl.style.setProperty('top', '-200px', 'important');
      }
  
      // Optional: stop observing once done
      obs.disconnect();
    }
  });
  
  // Start observing the whole document
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});